/** How long typing has to pause before a server-backed picker asks. */
const CAREGIVER_SEARCH_DEBOUNCE_MS = 250;

export const newVisitCaregiverSearch = {
    templateUrl: 'admin/views/new-visit-caregiver-search.html',
    bindings: {
        caregivers: '<',
        /**
         * `(term) => Promise<caregiver[]>`, and the reason this component no
         * longer needs `caregivers`.
         *
         * A picker shows a handful of names. Filtering a handed-in array meant
         * the caller first had to hold every caregiver a visit could go to, so
         * opening the form cost a list sized by the agency to render five rows.
         * Given this, the component asks for those five instead, and the caller
         * fetches nothing until somebody types.
         *
         * Callers that still pass `caregivers` keep the old local filtering.
         */
        searchCaregivers: '<',
        seeAllLabel: '<',
        allowEmptySearch: '<',
        isEditMode: '<',
        preSelectedItem: '<',
        isDisabled: '<',
        showError: '<',
        emptyCaregiversMessage: '<',
        handleSelect: '&',
        handleDeselect: '&',
        onSeeAllClick: '&',
        hideIcon: '<',
        caregiversLoading: '<'
    },
    //! @ngInject
    controller: function ($scope, $filter, $timeout) {
        $scope.results = [];
        $scope.resultsLength = 0;
        $scope.itemFilter = { val: '' };
        $scope.selectedIndex = -1;
        $scope.searching = false;

        let pendingSearch = null;
        // Answers can land out of order; only the newest keystroke's may show.
        let latestSearch = 0;

        const isServerBacked = () => angular.isFunction($scope.$ctrl.searchCaregivers);

        /**
         * Nothing to offer, so the box is greyed out and says why.
         *
         * True of both modes, and for the same reason: `caregivers` is the set a
         * caregiver could be picked from. Locally that is the set being filtered.
         * With a search function it is the caller's eligibility result - empty until a
         * certification is chosen, and empty when the chosen one has nobody
         * assignable - so it still answers exactly this question, and the box must
         * not invite typing that could only come back empty.
         */
        $scope.isUnavailable = () =>
            !(isServerBacked() && $scope.$ctrl.allowEmptySearch) &&
            (!$scope.$ctrl.caregivers || $scope.$ctrl.caregivers.length === 0);

        const setResults = (items) => {
            $scope.results = items;
            $scope.resultsLength = items.length;
        };

        const filterLocally = (term) =>
            Object.values($filter('filterByNameAndCode')($scope.$ctrl.caregivers, term));

        const search = (term) => {
            if (!isServerBacked()) {
                setResults(filterLocally(term));
                return;
            }

            if (pendingSearch !== null) {
                $timeout.cancel(pendingSearch);
                pendingSearch = null;
            }

            if (!term) {
                latestSearch++;
                $scope.searching = false;
                setResults([]);
                return;
            }

            const sequence = ++latestSearch;
            $scope.searching = true;

            pendingSearch = $timeout(() => {
                pendingSearch = null;
                $scope.$ctrl.searchCaregivers(term).then((items) => {
                    if (sequence !== latestSearch) return;
                    $scope.searching = false;
                    setResults(Array.isArray(items) ? items : []);
                }, () => {
                    // Leave the box empty rather than showing a stale answer.
                    if (sequence !== latestSearch) return;
                    $scope.searching = false;
                    setResults([]);
                });
            }, CAREGIVER_SEARCH_DEBOUNCE_MS);
        };

        $scope.onItemSelect = function (item) {
            $scope.itemFilter.val = '';
            $scope.selectedIndex = -1;
            latestSearch++;
            setResults([]);
            $scope.selectedItem = item;
            $scope.$ctrl.handleSelect({ caregiver: item });
        }

        $scope.onItemDeSelect = function () {
            $scope.itemFilter.val = '';
            $scope.selectedItem = null;
            $scope.selectedIndex = -1;
            latestSearch++;
            setResults([]);
            $scope.$ctrl.handleDeselect();
        }

        $scope.searchKeyUp = function (event) {
            switch (event.key) {
                case 'Enter': {
                    if ($scope.selectedIndex > -1 && $scope.selectedIndex < $scope.results.length) {
                        $scope.onItemSelect($scope.results[$scope.selectedIndex]);
                    }

                    break;
                }
                case 'Escape': {
                    $scope.onItemDeSelect();
                    break;
                }
                case 'ArrowDown': {
                    if ($scope.selectedIndex < $scope.results.length - 1) {
                        $scope.selectedIndex++;
                    }
                    break;
                }
                case 'ArrowUp': {
                    if ($scope.selectedIndex > -1) {
                        $scope.selectedIndex--;
                    }
                    break;
                }
                default: {
                    // Arrow keys move within the answer on screen; anything else
                    // is a new term, so the highlight goes back to nothing.
                    $scope.selectedIndex = -1;
                    search($scope.itemFilter.val);
                    break;
                }
            }
        }

        this.$onChanges = (changes) => {
            if ("preSelectedItem" in changes) {
                $scope.selectedItem = $scope.$ctrl.preSelectedItem;
            }
            if ("caregivers" in changes) {
                if (isServerBacked()) {
                    // The set a caregiver may be picked from has changed - the
                    // visit's certification, usually - so whatever is on screen was
                    // an answer to the old question. Drop it rather than leave rows
                    // standing that this visit may no longer be able to take.
                    latestSearch++;
                    $scope.itemFilter.val = '';
                    $scope.selectedIndex = -1;
                    setResults([]);
                } else {
                    // A local picker handed a new list re-filters what is typed.
                    search($scope.itemFilter.val);
                }
            }
        };

        this.$onInit = function () {
            if ($scope.$ctrl.preSelectedItem) {
                $scope.selectedItem = $scope.$ctrl.preSelectedItem;
            } else {
                $scope.selectedItem = null;
            }
        }

        $scope.$on('$destroy', () => {
            if (pendingSearch !== null) $timeout.cancel(pendingSearch);
        });
    }
};
